"use client";

import { useState, useEffect, useCallback } from "react";
import { X, Share, Zap, WifiOff, Maximize } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function detectStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function detectMobile() {
  if (typeof window === "undefined") return false;
  return (
    /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) || window.innerWidth < 768
  );
}

function detectIOS() {
  if (typeof window === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as unknown as { MSStream?: unknown }).MSStream
  );
}

export default function PWAInstallPrompt() {
  const t = useTranslations("PWA");
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS] = useState(detectIOS);
  const [isStandalone] = useState(detectStandalone);
  const [isMobile] = useState(detectMobile);

  useEffect(() => {
    if (isStandalone || !isMobile) return;

    // Check if user has dismissed prompt before
    const dismissed = localStorage.getItem("pwa-prompt-dismissed");
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 3 * 24 * 60 * 60 * 1000) return;
    }

    // Track visits — show on 2nd+ visit for iOS (better engagement)
    const visits =
      parseInt(localStorage.getItem("pwa-visit-count") || "0", 10) + 1;
    localStorage.setItem("pwa-visit-count", visits.toString());

    // Android/Chrome: listen for native install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => setShowPrompt(true), 2000);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS: show custom prompt after 2nd visit
    if (isIOS && visits >= 2) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isIOS, isMobile, isStandalone]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem("pwa-installed", "true");
    }
    setShowPrompt(false);
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
    localStorage.setItem("pwa-prompt-dismissed", Date.now().toString());
  }, []);

  if (!showPrompt || isStandalone || !isMobile) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/60 animate-fade-in"
        onClick={handleDismiss}
      />

      {/* Bottom sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9999] animate-sheet-up"
        role="dialog"
        aria-label={t("install_title")}
      >
        <div className="rounded-t-3xl bg-[var(--system-gray6)] px-6 pb-8 pt-3 safe-bottom">
          {/* Drag handle */}
          <div className="mx-auto mb-5 h-1 w-10 rounded-full bg-[var(--system-gray3)]" />

          {/* Close */}
          <button
            onClick={handleDismiss}
            className="absolute right-4 top-4 rounded-full bg-[var(--system-gray4)] p-1.5 text-[var(--label-secondary)] active:opacity-70"
            aria-label={t("close_button")}
          >
            <X className="h-4 w-4" />
          </button>

          {/* App icon + title */}
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl shadow-lg">
              <Image
                src="/pwa-icons/icon-192x192.png"
                alt={t("app_icon_alt")}
                width={64}
                height={64}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-[var(--label)]">
                {t("install_title")}
              </h2>
              <p className="mt-0.5 text-sm text-[var(--label-secondary)]">
                {t("install_subtitle")}
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="mt-5 flex gap-3">
            <BenefitChip
              icon={<WifiOff className="h-3.5 w-3.5" />}
              label={t("install_benefit_offline")}
            />
            <BenefitChip
              icon={<Zap className="h-3.5 w-3.5" />}
              label={t("install_benefit_fast")}
            />
            <BenefitChip
              icon={<Maximize className="h-3.5 w-3.5" />}
              label={t("install_benefit_fullscreen")}
            />
          </div>

          {/* iOS instructions */}
          {isIOS ? (
            <div className="mt-5 rounded-2xl bg-[var(--system-gray5)] p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--apple-blue)]/20">
                  <span className="text-base font-bold text-[var(--apple-blue)]">
                    1
                  </span>
                </div>
                <p className="text-sm text-[var(--label)]">
                  {t("ios_step1")}{" "}
                  <Share className="inline h-4 w-4 -translate-y-px text-[var(--apple-blue)]" />{" "}
                  {t("ios_step2")}
                </p>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--apple-blue)]/20">
                  <span className="text-base font-bold text-[var(--apple-blue)]">
                    2
                  </span>
                </div>
                <p className="text-sm text-[var(--label)]">{t("ios_step3")}</p>
              </div>
            </div>
          ) : (
            deferredPrompt && (
              <button
                onClick={handleInstall}
                className="mt-5 w-full rounded-2xl bg-[var(--apple-blue)] px-6 py-4 text-base font-semibold text-white transition-all active:brightness-90"
              >
                {t("install_button")}
              </button>
            )
          )}

          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            className="mt-3 w-full py-2 text-center text-sm text-[var(--label-tertiary)] active:text-[var(--label-secondary)]"
          >
            {t("install_dismiss")}
          </button>
        </div>
      </div>
    </>
  );
}

function BenefitChip({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex flex-1 flex-col items-center gap-1.5 rounded-xl bg-[var(--system-gray5)] px-2 py-3">
      <span className="text-[var(--apple-blue)]">{icon}</span>
      <span className="text-center text-[11px] font-medium leading-tight text-[var(--label-secondary)]">
        {label}
      </span>
    </div>
  );
}
